import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { submitLead } from "@/lib/api";
import { BUILDING_TYPES } from "@/data/content";

const EMPTY = { name: "", company: "", phone: "", email: "", building_type: "", surface: "", message: "" };

export default function LeadDialog({ children, leadType = "audit", title, description }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const isCallback = leadType === "callback";
  const heading = title || (isCallback ? "Être rappelé" : "Demander un audit énergétique");
  const desc =
    description ||
    (isCallback
      ? "Laissez vos coordonnées, un expert PRIMEC2E vous rappelle sous 24h."
      : "Décrivez votre bâtiment, nous revenons vers vous avec une première analyse.");

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Merci de renseigner votre nom, téléphone et email.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, lead_type: leadType, source: "dialog" });
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[520px] rounded-none border-midnight/10" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-midnight">{heading}</DialogTitle>
          <DialogDescription className="text-slate-500">{desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ld-name" className="text-xs uppercase tracking-wider text-slate-500">Nom *</Label>
              <Input id="ld-name" data-testid="lead-name" value={form.name} onChange={set("name")} className="rounded-none mt-1" />
            </div>
            <div>
              <Label htmlFor="ld-company" className="text-xs uppercase tracking-wider text-slate-500">Société</Label>
              <Input id="ld-company" data-testid="lead-company" value={form.company} onChange={set("company")} className="rounded-none mt-1" />
            </div>
            <div>
              <Label htmlFor="ld-phone" className="text-xs uppercase tracking-wider text-slate-500">Téléphone *</Label>
              <Input id="ld-phone" data-testid="lead-phone" value={form.phone} onChange={set("phone")} className="rounded-none mt-1" />
            </div>
            <div>
              <Label htmlFor="ld-email" className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
              <Input id="ld-email" type="email" data-testid="lead-email" value={form.email} onChange={set("email")} className="rounded-none mt-1" />
            </div>
          </div>
          {!isCallback && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Type de bâtiment</Label>
                <Select value={form.building_type} onValueChange={set("building_type")}>
                  <SelectTrigger data-testid="lead-building-type" className="rounded-none mt-1">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUILDING_TYPES.map((b) => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ld-surface" className="text-xs uppercase tracking-wider text-slate-500">Surface (m²)</Label>
                <Input id="ld-surface" data-testid="lead-surface" value={form.surface} onChange={set("surface")} className="rounded-none mt-1" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="ld-message" className="text-xs uppercase tracking-wider text-slate-500">Message</Label>
            <Textarea id="ld-message" data-testid="lead-message" value={form.message} onChange={set("message")} className="rounded-none mt-1" rows={3} />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="lead-submit"
            className="w-full rounded-none bg-emerald hover:bg-emerald-600 text-white font-medium py-6 group"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <span className="flex items-center gap-2">Envoyer ma demande <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
